import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { User } from "@/types";
import type { CreateUserInput } from "@/api/schemas/user";

function useUsersClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

function useInvalidateUsers() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.resource("users") });
}

// #214/#215 (B5-T2): Create-Response enthält das einmalige Gitea-Owner-Passwort
// (für die One-Time-Anzeige); null wenn keine Gitea-Anbindung / role=admin.
export interface CreatedUser extends User {
  giteaPassword: string | null;
}

export function useCreateUser(): UseMutationResult<CreatedUser, Error, CreateUserInput> {
  const getClient = useUsersClient();
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (input) => getClient().post<CreatedUser>("/users", input),
    onSuccess: invalidate,
  });
}

export interface ResetPasswordResponse {
  user: User;
  password: string;
}

export function useResetPassword(): UseMutationResult<
  ResetPasswordResponse,
  Error,
  { id: string; newPassword?: string }
> {
  const getClient = useUsersClient();
  return useMutation({
    // Ohne newPassword generiert der Server ein zufälliges Passwort; mit → eigenes (min 8).
    mutationFn: ({ id, newPassword }) =>
      getClient().post<ResetPasswordResponse>(
        `/users/${id}/reset-password`,
        newPassword ? { newPassword } : {},
      ),
  });
}

export function useDeleteUser(): UseMutationResult<void, Error, { id: string; archive?: boolean }> {
  const getClient = useUsersClient();
  const invalidate = useInvalidateUsers();
  return useMutation({
    // #199: ?archive=true sichert die Volumes der eigenen Agents vorher als tar.gz.
    mutationFn: ({ id, archive }) => getClient().delete<void>(`/users/${id}${archive ? "?archive=true" : ""}`),
    onSuccess: invalidate,
  });
}

export function useSetSupervisor(): UseMutationResult<
  User,
  Error,
  { id: string; supervisorId: string | null }
> {
  const getClient = useUsersClient();
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: ({ id, supervisorId }) =>
      getClient().patch<User>(`/users/${id}/supervisor`, { supervisorId }),
    onSuccess: invalidate,
  });
}
